import * as THREE from 'three';
import { rr, rnd, pick, TAU } from './util.js';
import { glyphTexture, blobTexture } from './textures.js';

/** Floating music notes (sprites) that drift up from the van's speakers. */
export class NoteParticles {
  constructor(scene, count = 18) {
    const glyphs = ['♪', '♫', '♩', '♬'];
    this.textures = glyphs.map((g) => glyphTexture(g, { size: 96, font: '700 84px Fredoka, Arial, sans-serif', color: '#ffffff', stroke: '#2a2350', strokeW: 9 }));
    this.items = [];
    const colors = ['#ff7fae', '#ffd166', '#7fe0c8', '#8ecbff', '#c9a3ff', '#ffffff'];
    for (let i = 0; i < count; i++) {
      const mat = new THREE.SpriteMaterial({ map: pick(this.textures), color: pick(colors), transparent: true, depthWrite: false, fog: false });
      const spr = new THREE.Sprite(mat);
      spr.visible = false;
      spr.renderOrder = 10;
      scene.add(spr);
      this.items.push({ spr, life: 0, max: 1, vx: 0, vy: 0, vz: 0, ph: 0 });
    }
    this.cursor = 0;
  }
  emit(x, y, z, vx = 0, vz = 0) {
    const it = this.items[this.cursor++ % this.items.length];
    it.spr.position.set(x, y, z);
    it.spr.material.map = pick(this.textures);
    it.life = it.max = rr(1.8, 2.8);
    it.vx = vx + rr(-0.5, 0.5);
    it.vz = vz + rr(-0.5, 0.5);
    it.vy = rr(1.2, 1.9);
    it.ph = rnd() * TAU;
    it.spr.visible = true;
  }
  update(dt, time) {
    for (const it of this.items) {
      if (it.life <= 0) continue;
      it.life -= dt;
      if (it.life <= 0) { it.spr.visible = false; continue; }
      const k = 1 - it.life / it.max;
      it.spr.position.x += (it.vx + Math.sin(time * 3 + it.ph) * 0.6) * dt;
      it.spr.position.z += it.vz * dt;
      it.spr.position.y += it.vy * dt * (1 - k * 0.5);
      const pop = Math.min(1, k * 8);
      const s = 0.95 * pop * (1 - Math.pow(k, 3) * 0.6);
      it.spr.scale.set(s, s, 1);
      it.spr.material.opacity = Math.min(1, (1 - k) * 2.2);
      it.spr.material.rotation = Math.sin(time * 2 + it.ph) * 0.25;
    }
  }
}

/** Cheap instanced puffs (exhaust, dust, chimney smoke). Fades by shrinking. */
export class PuffSystem {
  constructor(scene, max = 120, { color = '#ffffff', opacity = 0.85, emissive = 0.15 } = {}) {
    this.max = max;
    this.mesh = new THREE.InstancedMesh(
      new THREE.IcosahedronGeometry(1, 1),
      new THREE.MeshStandardMaterial({ color, roughness: 1, flatShading: true, transparent: true, opacity, emissive: color, emissiveIntensity: emissive, depthWrite: false }),
      max,
    );
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    scene.add(this.mesh);
    this.p = Array.from({ length: max }, () => ({ life: 0, max: 1, x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, s0: 1, s1: 1, rot: 0, spin: 0 }));
    this.cursor = 0;
    this._m = new THREE.Matrix4();
    this._q = new THREE.Quaternion();
    this._e = new THREE.Euler();
    this._pos = new THREE.Vector3();
    this._sc = new THREE.Vector3();
    for (let i = 0; i < max; i++) this.mesh.setMatrixAt(i, this._m.makeScale(0, 0, 0));
  }
  emit(x, y, z, vx, vy, vz, life, s0, s1) {
    const p = this.p[this.cursor++ % this.max];
    p.life = p.max = life;
    p.x = x; p.y = y; p.z = z;
    p.vx = vx; p.vy = vy; p.vz = vz;
    p.s0 = s0; p.s1 = s1;
    p.rot = rnd() * TAU;
    p.spin = rr(-1.5, 1.5);
  }
  update(dt, wind = 0) {
    for (let i = 0; i < this.max; i++) {
      const p = this.p[i];
      if (p.life <= 0) {
        if (p.s0 !== -1) { this._m.makeScale(0, 0, 0); this.mesh.setMatrixAt(i, this._m); p.s0 = -1; }
        continue;
      }
      p.life -= dt;
      p.x += (p.vx + wind) * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      p.rot += p.spin * dt;
      const k = 1 - Math.max(p.life, 0) / p.max;
      // grow then shrink away in the last third
      const grow = p.s0 + (p.s1 - p.s0) * Math.min(1, k * 1.4);
      const fade = k > 0.66 ? 1 - (k - 0.66) / 0.34 : 1;
      const s = Math.max(0.0001, grow * (0.25 + 0.75 * Math.min(1, k * 6)) * fade);
      this._e.set(p.rot, p.rot * 0.7, 0);
      this._q.setFromEuler(this._e);
      this._pos.set(p.x, p.y, p.z);
      this._sc.setScalar(s);
      this._m.compose(this._pos, this._q, this._sc);
      this.mesh.setMatrixAt(i, this._m);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}
